import React, { useState, useMemo } from 'react';
import api from '../../ui/api';
import useData from '../useData';
import PageShell from '../../ui/PageShell';
import Card from '../../ui/Card';
import Table from '../../ui/Table';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/PageShell';
import { Field, Input } from '../../ui/Form';
import './auditLog.css';

/**
 * سجل العمليات — who did what, to whom, when, and what it was before.
 * Read-only: the rows are written by the API around every successful
 * change (api/src/lib/audit.js) and the table refuses edits/deletes.
 */

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const ROLE_LABEL = { admin: 'مدير', register: 'موجه', teacher: 'معلم', student: 'طالب / ولي أمر' };

// Readable names for the fields that show up in "before → after".
const FIELD_LABEL = {
  name: 'الاسم', phone: 'الهاتف', role: 'الدور', status: 'الحالة', title: 'العنوان',
  marks_obtained: 'العلامة', remarks: 'ملاحظة', total_marks: 'العلامة العظمى', weight: 'الوزن',
  date: 'التاريخ', semester: 'الفصل', tuition_fee: 'القسط', transport_fee: 'أجور النقل',
  birth_date: 'تاريخ الميلاد', national_id: 'الرقم الوطني', student_phone: 'هاتف الطالب',
  registration_number: 'رقم التسجيل', notes: 'ملاحظات', gender: 'الجنس',
  father_name: 'اسم الأب', father_phone: 'هاتف الأب', mother_name: 'اسم الأم', mother_phone: 'هاتف الأم',
  address: 'العنوان', last_name: 'الكنية', homeroom_teacher_id: 'معلم الصف', grade_id: 'الصف',
  batch_id: 'الفوج', sort_order: 'الترتيب', note: 'ملاحظة', bus_number: 'رقم الباص',
  driver_name: 'السائق', monthly_fee: 'الأجر الشهري',
};

const VALUE_LABEL = {
  admin: 'مدير', register: 'موجه', teacher: 'معلم', student: 'طالب',
  active: 'نشط', inactive: 'غير نشط', absent: 'غائب', late: 'متأخر', present: 'حاضر',
};
const showValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return VALUE_LABEL[v] || String(v);
};

/** Badge colour from the action key, not the Arabic label. */
function tone(action = '') {
  if (/\.(delete|leave|lock|deactivate|unpublish|revoke)$/.test(action)) return 'danger';
  if (action.startsWith('payment.') || /\.(publish|unlock)$/.test(action)) return 'success';
  if (/\.(update|swap|transfer|family|transport)$/.test(action)) return 'warning';
  if (/\.(create|issue|take|bulk|send|create_identity)$/.test(action)) return 'info';
  return 'neutral';
}

// Numbers, phones and dates keep left-to-right order inside Arabic text.
const dirOf = (v) => (/[؀-ۿ]/.test(String(v)) ? 'rtl' : 'ltr');

const parse = (s) => { try { return s ? JSON.parse(s) : null; } catch { return null; } };

export default function AuditLog() {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [open, setOpen] = useState(null);

  const { data, loading, error } = useData(
    () => api.auditLog({ from, to, limit: 2000 }),
    [from, to]
  );

  const rows = useMemo(() => (data || []).map((r) => ({
    ...r,
    who: r.user_name || '—',
    role_label: ROLE_LABEL[r.user_role] || '—',
    target: r.entity_name || '—',
    summary_text: r.summary || (r.changes ? Object.keys(parse(r.changes) || {}).map((k) => FIELD_LABEL[k] || k).join('، ') : '—'),
  })), [data]);

  return (
    <PageShell title="سجل العمليات" description="من قام بكل عملية، ومتى، وماذا تغيّر">
      <Card>
        <div className="audit-range">
          <Field label="من تاريخ">
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="إلى تاريخ">
            <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Card>

      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Card padded={false}>
        <Table
          keyField="id"
          rows={rows}
          loading={loading}
          emptyText="لا توجد عمليات في هذه الفترة"
          searchPlaceholder="ابحث بالاسم أو العملية أو التفاصيل..."
          searchableKeys={['who', 'label', 'target', 'summary_text', 'ip_address']}
          exportName={`audit-log-${from}-${to}`}
          renderActions={(r) => (
            <button type="button" className="ui-icon-btn" title="التفاصيل" onClick={() => setOpen(r)}>⤢</button>
          )}
          actionsLabel="تفاصيل"
          columns={[
            { key: 'created_at', label: 'الوقت', nowrap: true, filterable: false, formatValue: fmt },
            { key: 'who', label: 'المستخدم', nowrap: true },
            { key: 'role_label', label: 'الدور', nowrap: true, align: 'center' },
            { key: 'label', label: 'العملية', nowrap: true, badge: (v, row) => tone(row?.action) },
            { key: 'target', label: 'على', width: 2, clamp: 1, filterable: false },
            { key: 'summary_text', label: 'التفاصيل', width: 2, clamp: 1, filterable: false },
          ]}
        />
      </Card>

      {open && <AuditDetail row={open} onClose={() => setOpen(null)} />}
    </PageShell>
  );
}

function AuditDetail({ row, onClose }) {
  const changes = parse(row.changes);
  const details = parse(row.details);
  const removed = details?.removed;
  const sent = removed ? details.body : details;

  return (
    <Modal
      open
      size="lg"
      title={row.label}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>إغلاق</Button>}
    >
      <dl className="audit-meta">
        <div><dt>الوقت</dt><dd dir="ltr">{fmt(row.created_at)}</dd></div>
        <div><dt>المستخدم</dt><dd>{row.user_name || '—'} ({ROLE_LABEL[row.user_role] || '—'})</dd></div>
        <div><dt>على</dt><dd>{row.entity_name || '—'}</dd></div>
        {row.summary && <div><dt>الملخص</dt><dd><bdi>{row.summary}</bdi></dd></div>}
        <div><dt>IP</dt><dd dir="ltr">{row.ip_address || '—'}</dd></div>
      </dl>

      {changes && (
        <>
          <h4 className="audit-h">التغييرات</h4>
          <table className="audit-table">
            <thead><tr><th>الحقل</th><th>قبل</th><th>بعد</th></tr></thead>
            <tbody>
              {Object.entries(changes).map(([k, [a, b]]) => (
                <tr key={k}>
                  <td>{FIELD_LABEL[k] || k}</td>
                  <td className="audit-before"><bdi dir={dirOf(showValue(a))}>{showValue(a)}</bdi></td>
                  <td className="audit-after"><bdi dir={dirOf(showValue(b))}>{showValue(b)}</bdi></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {removed && <KeyValues title="البيانات المحذوفة" obj={removed} />}
      {sent && !changes && <KeyValues title="البيانات" obj={sent} />}
    </Modal>
  );
}

function KeyValues({ title, obj }) {
  const entries = Object.entries(obj || {}).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object');
  if (!entries.length) return null;
  return (
    <>
      <h4 className="audit-h">{title}</h4>
      <table className="audit-table">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k}><td>{FIELD_LABEL[k] || k}</td><td><bdi dir={dirOf(showValue(v))}>{showValue(v)}</bdi></td></tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
